import axios from 'axios';
import { BaseModal } from 'components/modals/BaseModal';
import { ConfirmOrCancel } from 'components/forms/ConfirmOrCancel';
import { Color } from 'core';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import useStore from 'zstore';
import { pluralize } from 'utils/stringUtils';
import { normalizeJSONAPIResponse } from 'utils/modelUtils';
import { AnalyticsService } from 'utils/AnalyticsService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDesktop, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { ContentIntelligenceItem } from './types';
import { GoalType } from 'models/set';

type ExtractableGoalType = Extract<GoalType, 'set' | 'assessment'>;

interface ExtractToSetModalProps {
  items: ContentIntelligenceItem[];
  onClose: (success?: boolean) => void;
}

export const ExtractToSetModal = ({ items, onClose }: ExtractToSetModalProps) => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const partnerId = user?.primaryPartner.id;
  const { makeToast } = useStore();

  const [setName, setSetName] = useState(
    () => {
      const now = new Date();
      const time = now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase();
      return `Extracted Items — ${now.toLocaleDateString()} ${time}`;
    }
  );
  const [goalType, setGoalType] = useState<ExtractableGoalType>('set');
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const extractableItems = useMemo(
    () => items.filter(item => item.template && item.goalType !== 'survey'),
    [items]
  );
  const blankCount = items.filter(item => !item.template).length;
  const surveyCount = items.filter(item => item.goalType === 'survey' && item.template).length;
  const typeName = goalType === 'set' ? 'Set' : 'Assessment';

  const handleConfirm = async () => {
    if (!partnerId || !setName.trim() || extractableItems.length === 0) return;

    setIsPending(true);
    setError(null);

    let succeeded = false;
    try {
      // Step 1: Create the set
      const setResponse = await axios.post('/api/v3/sets', {
        learn_version: 4,
        goal_type: goalType,
        name: setName.trim(),
        partner_id: partnerId,
      });

      const newSet = normalizeJSONAPIResponse(setResponse.data);

      // Step 2: Clone items into the new set
      let clonedCount = extractableItems.length;
      try {
        const cloneResponse = await axios.post(`/api/v3/partners/${partnerId}/clone_items`, {
          target_set_id: newSet.id,
          item_ids: extractableItems.map(item => Number(item.id)),
        });
        clonedCount = cloneResponse.data.cloned_count;
      } catch (cloneErr) {
        setError(t('Assignment was created but items could not be cloned. Please try again.'));
        return;
      }

      AnalyticsService.getInstance().track('content_intelligence_extract_to_set', {
        item_count: clonedCount,
      });

      makeToast({
        text: t('Created assignment "{{setName}}" with {{count}}', {
          setName: newSet.name,
          count: pluralize(clonedCount, 'item'),
        }),
        link: { label: t('View'), href: `/nav/sets/${newSet.id}/items`, target: '_blank' },
        isError: false,
        duration: 60,
      });

      succeeded = true;
    } catch (err) {
      setError(t('Failed to create assignment. Please try again.'));
    } finally {
      setIsPending(false);
    }
    if (succeeded) onClose(true);
  };

  return (
    <BaseModal title={t('Extract to New {{type}}', { type: typeName })} onClose={() => onClose()}>
      <ModalBody>
        <Label htmlFor="extract-set-name">{t('{{type}} Name', { type: typeName })}</Label>
        <NameInput
          id="extract-set-name"
          type="text"
          value={setName}
          onChange={(e) => setSetName(e.target.value)}
          autoFocus
        />
        <Label id="type-card-group-label">{t('{{type}} Type', { type: typeName })}</Label>
        <TypeCardRow role="radiogroup" aria-labelledby="type-card-group-label">
          <TypeCard
            isSelected={goalType === 'set'}
            onClick={() => setGoalType('set')}
            role="radio"
            aria-checked={goalType === 'set'}
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setGoalType('set'); } }}
          >
            <TypeIconContainer color={Color.iconBlue}>
              <FontAwesomeIcon icon={faDesktop} color="#fff" />
            </TypeIconContainer>
            <TypeCardTitle>{t('Learning Set')}</TypeCardTitle>
            <TypeCardDescription>
              {t('Adaptive content designed for repeated practice.')}
            </TypeCardDescription>
          </TypeCard>
          <TypeCard
            isSelected={goalType === 'assessment'}
            onClick={() => setGoalType('assessment')}
            role="radio"
            aria-checked={goalType === 'assessment'}
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setGoalType('assessment'); } }}
          >
            <TypeIconContainer color={Color.iconGreen}>
              <FontAwesomeIcon icon={faCheckCircle} color="#fff" />
            </TypeIconContainer>
            <TypeCardTitle>{t('Assessment')}</TypeCardTitle>
            <TypeCardDescription>
              {t('One-time pass through the material. Produces a score.')}
            </TypeCardDescription>
          </TypeCard>
        </TypeCardRow>
        <ItemCount>
          {pluralize(extractableItems.length, 'item')} {t('will be cloned into the new {{type}}', { type: typeName.toLowerCase() })}
        </ItemCount>
        {(blankCount > 0 || surveyCount > 0) && (
          <WarningText>
            {[
              blankCount > 0 && pluralize(blankCount, 'blank item'),
              surveyCount > 0 && pluralize(surveyCount, 'survey item'),
            ].filter(Boolean).join(' and ')}{' '}
            {t('will be skipped.')}
          </WarningText>
        )}
        <Note>{t('Items will be cloned — originals remain in their current assignments.')}</Note>
        {error && <ErrorText>{error}</ErrorText>}
        <ConfirmOrCancel
          confirmText={t('Create {{type}}', { type: typeName })}
          handleConfirmClick={handleConfirm}
          handleCancelClick={() => onClose()}
          confirmIsDisabled={!setName.trim() || extractableItems.length === 0}
          confirmIsPending={isPending}
        />
      </ModalBody>
    </BaseModal>
  );
};

const ModalBody = styled.div`
  min-width: 400px;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  margin-top: 16px;
  margin-bottom: 6px;
  color: ${Color.primaryBlack};
`;

const NameInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${Color.lightGray};
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  margin-bottom: 4px;

  &:focus {
    border-color: ${Color.primaryBlue};
  }
`;

const TypeCardRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const TypeCard = styled.div<{ isSelected: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 16px;
  border-radius: 8px;
  cursor: pointer;
  border: 2px solid ${({ isSelected }) => (isSelected ? Color.primaryBlue : '#e5e5e5')};
  background: ${({ isSelected }) => (isSelected ? '#f8f9ff' : '#fff')};
  transition: border-color 0.15s, background 0.15s;
`;

const TypeIconContainer = styled.div<{ color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  font-size: 18px;
`;

const TypeCardTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${Color.primaryBlack};
  margin-bottom: 4px;
`;

const TypeCardDescription = styled.span`
  font-size: 13px;
  color: ${Color.textGray};
`;

const ItemCount = styled.p`
  margin: 4px 0 4px;
  font-size: 14px;
  color: ${Color.darkGray};
`;

const WarningText = styled.p`
  font-size: 13px;
  color: ${Color.orange};
  margin: 4px 0 0;
`;

const Note = styled.p`
  font-size: 13px;
  color: ${Color.textGray};
  margin: 4px 0 8px;
`;

const ErrorText = styled.p`
  color: ${Color.red};
  font-size: 14px;
  margin: 12px 0 0;
`;
